import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { debounce } from 'es-toolkit'
import { cloneDeep, get, set } from 'es-toolkit/compat'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ThemeObject } from '@tokens-studio/types'
import { toast } from 'sonner'
import { Button, Checkbox, Menu, MenuItem, Popper, Skeleton, Spinner, cn, isHexColor, useModal } from '@dashboard/ui'
import { WebsiteRead } from '@/api/dashboard'
import { useFeatureFlags, useGenerateCompanyPath } from '@/libs'
import {
  ThemeOverrideGroup,
  ThemeRawModal,
  ThemeTokenControl,
  fetchProcessedTheme,
  getThemePaths,
  getTokenPath,
  getTokenValue,
} from '@/hub-editor'
import { useCurrentUser } from '@/api/useCurrentUser'
import { Theme, fetchWebsiteDefaultTheme, themeQueryOpts } from 'src/layouts/token-studio'
import { mergeThemeWithValues } from '@/layouts/token-studio/libs/theme'
import { ToastSeverity, useToast } from '@/ui'

interface ThemeEditorProps {
  segmentTemplateId?: string
  website: WebsiteRead
  updateTheme: (css: string) => void
  findToken: (token: string) => void
  handleTokensPage: () => void
}

const ALLOW_TOKENS_SUFFIX = ['tab-accent']
const IGNORE_TOKENS_SUFFIX = ['accent', 'disabled', 'hover']
const THEME_TYPES = ['color-light', 'color-dark', 'neutral-light', 'neutral-dark']

export const ThemeEditor = ({
  segmentTemplateId,
  website,
  updateTheme,
  findToken,
  handleTokensPage,
}: ThemeEditorProps) => {
  const ql = useQueryClient()
  const [isProcessing, setIsProcessing] = useState(0)
  const showToast = useToast()
  const { t } = useTranslation('hub-theme')
  const { isStaff, isOutsourcer } = useCurrentUser()
  const { featureFlags } = useFeatureFlags()
  const { gameId } = useGenerateCompanyPath()

  const segmentedThemeQueryOpts = segmentTemplateId
    ? themeQueryOpts(gameId, website, segmentTemplateId)
    : themeQueryOpts(gameId, website)

  const { data: themeRaw, isLoading, isFetching } = useQuery(segmentedThemeQueryOpts)

  const [defaultTheme, setDefaultTheme] = useState<Theme | null>(null)

  const openThemeRawModal = useModal(props => (
    <ThemeRawModal segmentedThemeQueryKey={segmentedThemeQueryOpts.queryKey} theme={themeRaw} {...props} />
  ))

  const editableTokens = useMemo(
    () => ({
      'global.palette': themeRaw
        ? Object.entries(themeRaw.theme.global.palette || {}).filter(
            ([key]) => !IGNORE_TOKENS_SUFFIX.some(suffix => key.endsWith(suffix)),
          )
        : [],
      'sem.component': themeRaw
        ? Object.entries(themeRaw.theme.sem.component || {}).filter(
            ([key]) =>
              !IGNORE_TOKENS_SUFFIX.some(
                suffix => key.endsWith(suffix) && !ALLOW_TOKENS_SUFFIX.some(suffix => key.endsWith(suffix)),
              ),
          )
        : [],
      'sem.bg': themeRaw
        ? Object.entries(themeRaw.theme.sem.bg || {}).filter(
            ([key]) => !IGNORE_TOKENS_SUFFIX.some(suffix => key.endsWith(suffix)),
          )
        : [],
      'sem.surface': themeRaw
        ? Object.entries(themeRaw.theme.sem.surface || {}).filter(
            ([key]) => !IGNORE_TOKENS_SUFFIX.some(suffix => key.endsWith(suffix)),
          )
        : [],
      'sem.border': themeRaw
        ? Object.entries(themeRaw.theme.sem.border || {}).filter(
            ([key]) => !IGNORE_TOKENS_SUFFIX.some(suffix => key.endsWith(suffix)),
          )
        : [],
      'sem.text': themeRaw
        ? Object.entries(themeRaw.theme.sem.text || {}).filter(
            ([key]) => !IGNORE_TOKENS_SUFFIX.some(suffix => key.endsWith(suffix)),
          )
        : [],
    }),
    [themeRaw],
  )

  const onChangeToken = useCallback(
    (path: string, value: string) => {
      ql.setQueryData<ThemeObject>(segmentedThemeQueryOpts.queryKey, currentTheme => {
        if (!currentTheme) {
          return currentTheme
        }

        const changedTheme = cloneDeep(currentTheme)

        if (value && isHexColor(value)) {
          set(changedTheme, getTokenPath(path), {
            value,
            type: 'color',
            $override: true,
          })
        } else {
          if (defaultTheme) {
            set(changedTheme, getTokenPath(path), get(defaultTheme, getTokenPath(path)))
          }
        }

        return changedTheme
      })
    },
    [website, themeRaw],
  )

  const handleUpdateTheme = useCallback(
    debounce(theme => {
      setIsProcessing(p => p + 1)
      fetchProcessedTheme(website, theme)
        .then(theme => updateTheme(theme.css))
        .finally(() => {
          setIsProcessing(p => p - 1)
        })
    }, 250),
    [],
  )

  const onClickChangeThemeType = async (type: string) => {
    setIsProcessing(p => p + 1)

    const fetchedTheme = await fetchWebsiteDefaultTheme(website, type)

    ql.setQueryData<Theme>(segmentedThemeQueryOpts.queryKey, currentTheme => {
      return currentTheme
        ? mergeThemeWithValues({
            from: currentTheme,
            to: fetchedTheme,
            keep: ['theme.global.palette.palette-brand-a', 'theme.global.palette.palette-brand-b'],
          })
        : fetchedTheme
    })

    setIsProcessing(p => p - 1)
  }

  const onClickSync = async (from: 'prod' | 'preview') => {
    if (!themeRaw?.name) {
      showToast({ message: 'No theme selected', severity: ToastSeverity.error })
      return
    }

    setIsProcessing(p => p + 1)

    toast.promise(
      async () => {
        try {
          const fetchedTheme = await fetchWebsiteDefaultTheme(website, themeRaw?.name, from === 'preview')

          ql.setQueryData<Theme>(segmentedThemeQueryOpts.queryKey, currentTheme => {
            if (!currentTheme) {
              return fetchedTheme
            }

            const keep = getThemePaths(currentTheme)

            return mergeThemeWithValues({
              from: currentTheme,
              to: fetchedTheme,
              keep,
            })
          })
        } finally {
          setIsProcessing(p => p - 1)
        }
      },
      {
        loading: 'Loading...',
        success: 'Theme successfully synchronized',
        error: error => (error instanceof Error ? error.message : 'Error'),
      },
    )
  }

  useEffect(() => {
    if (themeRaw?.name) {
      setIsProcessing(p => p + 1)
      fetchWebsiteDefaultTheme(website, themeRaw.name)
        .then(theme => {
          setDefaultTheme(theme)
        })
        .finally(() => {
          setIsProcessing(p => p - 1)
        })
    }
  }, [themeRaw])

  useEffect(() => {
    handleUpdateTheme(themeRaw)
  }, [themeRaw])

  if (isLoading || isFetching) {
    return (
      <div className="flex w-full flex-col gap-3 p-3 text-text-quarterary">
        <div className="flex flex-col gap-1">
          <Skeleton className="h-[18px] w-1/4" />
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="flex flex-col gap-1">
          <Skeleton className="h-[18px] w-1/4" />
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="flex flex-col gap-1">
          <Skeleton className="h-[18px] w-1/4" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 p-3">
      <div className="flex flex-col gap-3">
        {editableTokens['global.palette'].map(([path]) => {
          const tokenValue = themeRaw ? getTokenValue(themeRaw, `global.palette.${path}`) || '' : ''

          if (!tokenValue) {
            if (isStaff || isOutsourcer) {
              return (
                <div key={`control-${path}`} className="rounded ring-2 ring-pink-500 ring-offset-4">
                  <ThemeTokenControl
                    disabled={!!isProcessing}
                    token={{
                      path,
                      value: {
                        value: tokenValue,
                      },
                    }}
                    findToken={findToken}
                    onChange={value => onChangeToken(`global.palette.${path}`, value)}
                  />
                </div>
              )
            }
            return null
          }

          return (
            <ThemeTokenControl
              key={`control-${path}`}
              disabled={!!isProcessing}
              token={{
                path,
                value: {
                  value: tokenValue,
                },
              }}
              findToken={findToken}
              onChange={value => onChangeToken(`global.palette.${path}`, value)}
            />
          )
        })}
      </div>

      <div className="flex flex-col gap-1">
        <div className="font-medium text-text-tertiary">{t('theme.schemes.title')}</div>
        <div className="flex flex-col gap-2">
          {THEME_TYPES.map(type => (
            <Checkbox
              type="radio"
              name="type"
              checked={type === themeRaw?.name}
              disabled={!!isProcessing}
              onChange={() => onClickChangeThemeType(type)}
            >
              {t(`theme.schemes.${type}`)}
            </Checkbox>
          ))}
        </div>
      </div>

      <div
        className={cn(
          '-mb-3 flex select-none items-center gap-1.5 text-text-tertiary opacity-0 transition-all',
          isProcessing && 'opacity-100',
        )}
      >
        <Spinner className="size-3.5" />
        <span>{t('theme.processing')}</span>
      </div>

      <div className={cn(isProcessing && 'pointer-events-none animate-pulse')}>
        <ThemeOverrideGroup
          label={t('theme.groups.components')}
          tokens={{ namespace: 'sem.component', array: editableTokens['sem.component'] }}
          disabled={!!isProcessing}
          findToken={findToken}
          onChange={onChangeToken}
        />

        <ThemeOverrideGroup
          label={t('theme.groups.bg')}
          tokens={{ namespace: 'sem.bg', array: editableTokens['sem.bg'] }}
          disabled={!!isProcessing}
          findToken={findToken}
          onChange={onChangeToken}
        />

        <ThemeOverrideGroup
          label={t('theme.groups.surface')}
          tokens={{ namespace: 'sem.surface', array: editableTokens['sem.surface'] }}
          disabled={!!isProcessing}
          findToken={findToken}
          onChange={onChangeToken}
        />

        <ThemeOverrideGroup
          label={t('theme.groups.border')}
          tokens={{ namespace: 'sem.border', array: editableTokens['sem.border'] }}
          disabled={!!isProcessing}
          findToken={findToken}
          onChange={onChangeToken}
        />

        <ThemeOverrideGroup
          label={t('theme.groups.text')}
          tokens={{ namespace: 'sem.text', array: editableTokens['sem.text'] }}
          disabled={!!isProcessing}
          findToken={findToken}
          onChange={onChangeToken}
        />
      </div>

      {(isOutsourcer || isStaff) && (
        <div className="flex flex-col justify-between gap-2">
          <div
            className={cn(
              'flex flex-col gap-1.5 rounded-lg border-2 border-pink-500 p-3',
              isProcessing && 'pointer-events-none animate-pulse',
            )}
          >
            <Popper
              popper={props => (
                <Menu {...props}>
                  <MenuItem
                    onClick={() => {
                      onClickSync('prod')
                      props.onClose()
                    }}
                  >
                    Sync theme
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      onClickSync('preview')
                      props.onClose()
                    }}
                  >
                    Sync theme from .preview
                  </MenuItem>
                </Menu>
              )}
            >
              <Button className="w-full">Sync</Button>
            </Popper>
            <Button className="w-full" variant="outline" onClick={openThemeRawModal}>
              JSON
            </Button>
          </div>
        </div>
      )}

      {!segmentTemplateId && featureFlags.theme_editor_token_studio_enabled && (
        <Button onClick={handleTokensPage} variant="outline">
          Token Studio
        </Button>
      )}
    </div>
  )
}
